import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Facebook, Instagram, Youtube, Save, ExternalLink, ArrowLeft, LogOut } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminSocialMedia = () => {
  const navigate = useNavigate();
  const { user, loading, logout } = useAuth();
  const [socialLinks, setSocialLinks] = useState({
    facebook_url: '',
    instagram_url: '',
    youtube_url: ''
  });
  const [dataLoading, setDataLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!loading && !user) {
      navigate('/admin/login');
    } else if (user) {
      fetchSocialLinks();
    }
  }, [user, loading, navigate]);

  const fetchSocialLinks = async () => {
    try {
      const response = await axios.get(`${API}/social-media`);
      setSocialLinks(response.data);
    } catch (error) {
      console.error('Error fetching social links:', error);
      toast.error('Failed to load social media links');
    } finally {
      setDataLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setSocialLinks(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleLogout = async () => {
    await logout();
    toast.success('Logged out successfully');
    navigate('/');
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/social-media`, socialLinks, { withCredentials: true });
      toast.success('Social media links updated successfully!');
    } catch (error) {
      console.error('Error saving social links:', error);
      toast.error('Failed to save social media links');
    } finally {
      setSaving(false);
    }
  };

  const validateUrl = (url) => {
    if (!url) return true; // Empty is OK
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="admin-container">
      {/* Header */}
      <header className="admin-header">
        <div className="admin-header-content">
          <div className="admin-logo-section" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
            <img 
              src="https://customer-assets.emergentagent.com/job_lethbridge-angels/artifacts/znfka10j_RA-blacktransparent%20cropped%20no%20vol.png" 
              alt="Rebel Angels" 
              className="admin-brand-logo"
            />
          </div>
          <div className="admin-user-section">
            <img src={user.picture} alt={user.name} className="admin-user-avatar" />
            <span className="admin-user-name">{user.name}</span>
            <Button variant="outline" className="admin-logout-button" onClick={handleLogout}>
              <LogOut className="button-icon" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="admin-main">
        <div className="admin-content">
          <div className="admin-page-header">
            <Button className="admin-add-button" onClick={() => navigate('/admin/dashboard')}>
              <ArrowLeft className="button-icon" />
              Back to Dashboard
            </Button>
          </div>

          <div className="admin-title-row">
            <h1 className="admin-page-title">Social Media Links</h1>
            <Button
              onClick={handleSave}
              disabled={saving || 
                (socialLinks.facebook_url && !validateUrl(socialLinks.facebook_url)) ||
                (socialLinks.instagram_url && !validateUrl(socialLinks.instagram_url)) ||
                (socialLinks.youtube_url && !validateUrl(socialLinks.youtube_url))
              }
              className="admin-add-button"
            >
              <Save className="button-icon" />
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>

          <div className="admin-social-grid">
            {/* Facebook Card */}
            <Card className="admin-social-card">
              <CardContent className="admin-social-card-content">
                <div className="admin-social-header">
                  <Facebook className="admin-social-icon text-blue-500" />
                  <h3 className="admin-social-title">Facebook</h3>
                </div>
                <div className="admin-form-group">
                  <Input
                    type="url"
                    placeholder="https://www.facebook.com/YourPage"
                    value={socialLinks.facebook_url}
                    onChange={(e) => handleInputChange('facebook_url', e.target.value)}
                    className="admin-form-input"
                  />
                  {socialLinks.facebook_url && !validateUrl(socialLinks.facebook_url) && (
                    <p className="text-red-400 text-sm mt-2">Please enter a valid URL</p>
                  )}
                </div>
                {socialLinks.facebook_url && validateUrl(socialLinks.facebook_url) && (
                  <div className="admin-social-preview">
                    <a
                      href={socialLinks.facebook_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="admin-preview-link text-blue-400 hover:text-blue-300"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Preview Link
                    </a>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Instagram Card */}
            <Card className="admin-social-card">
              <CardContent className="admin-social-card-content">
                <div className="admin-social-header">
                  <Instagram className="admin-social-icon text-pink-500" />
                  <h3 className="admin-social-title">Instagram</h3>
                </div>
                <div className="admin-form-group">
                  <Input
                    type="url"
                    placeholder="https://www.instagram.com/yourusername"
                    value={socialLinks.instagram_url}
                    onChange={(e) => handleInputChange('instagram_url', e.target.value)}
                    className="admin-form-input"
                  />
                  {socialLinks.instagram_url && !validateUrl(socialLinks.instagram_url) && (
                    <p className="text-red-400 text-sm mt-2">Please enter a valid URL</p>
                  )}
                </div>
                {socialLinks.instagram_url && validateUrl(socialLinks.instagram_url) && (
                  <div className="admin-social-preview">
                    <a
                      href={socialLinks.instagram_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="admin-preview-link text-pink-400 hover:text-pink-300"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Preview Link
                    </a>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* YouTube Card */}
            <Card className="admin-social-card">
              <CardContent className="admin-social-card-content">
                <div className="admin-social-header">
                  <Youtube className="admin-social-icon text-red-500" />
                  <h3 className="admin-social-title">YouTube</h3>
                </div>
                <div className="admin-form-group">
                  <Input
                    type="url"
                    placeholder="https://www.youtube.com/channel/YourChannel"
                    value={socialLinks.youtube_url}
                    onChange={(e) => handleInputChange('youtube_url', e.target.value)}
                    className="admin-form-input"
                  />
                  {socialLinks.youtube_url && !validateUrl(socialLinks.youtube_url) && (
                    <p className="text-red-400 text-sm mt-2">Please enter a valid URL</p>
                  )}
                </div>
                {socialLinks.youtube_url && validateUrl(socialLinks.youtube_url) && (
                  <div className="admin-social-preview">
                    <a
                      href={socialLinks.youtube_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="admin-preview-link text-red-400 hover:text-red-300"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Preview Link
                    </a>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Instructions Section */}
          <div style={{ marginBottom: '2rem' }}>
            <Card style={{ background: 'rgba(229, 0, 0, 0.1)', border: '1px solid var(--retro-red)' }}>
              <CardContent style={{ padding: '1.5rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
                  <ExternalLink style={{ color: 'var(--retro-red)' }} size={20} />
                  <h3 style={{ color: 'var(--retro-red)', margin: 0 }}>Social Media Configuration</h3>
                </div>
                <p style={{ color: 'var(--retro-cream)', margin: '0 0 1.5rem 0', fontSize: '0.9rem' }}>
                  • Add your social media URLs in the cards above<br/>
                  • Links will automatically appear in the contact section of your website<br/>
                  • Leave fields empty to hide specific social media icons<br/>
                  • Use the preview links to test your URLs before saving
                </p>
                <div>
                  <h4 style={{ color: 'var(--retro-red)', margin: '0 0 0.75rem 0', fontSize: '0.95rem', fontWeight: 'bold' }}>Current Status:</h4>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem' }}>
                    <span className={`admin-status-badge ${socialLinks.facebook_url ? 'admin-status-active' : 'admin-status-inactive'}`}>
                      <Facebook className="w-3 h-3" />
                      Facebook {socialLinks.facebook_url ? '✓' : '✗'}
                    </span>
                    <span className={`admin-status-badge ${socialLinks.instagram_url ? 'admin-status-active' : 'admin-status-inactive'}`}>
                      <Instagram className="w-3 h-3" />
                      Instagram {socialLinks.instagram_url ? '✓' : '✗'}
                    </span>
                    <span className={`admin-status-badge ${socialLinks.youtube_url ? 'admin-status-active' : 'admin-status-inactive'}`}>
                      <Youtube className="w-3 h-3" />
                      YouTube {socialLinks.youtube_url ? '✓' : '✗'}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminSocialMedia;